import { useCallback, useEffect, useRef, useState } from 'react';
import useOperationPdf from 'screens/Applications&WorkOrders/Hooks/useOperationPdf';
import useSavedPdfOrientation from 'screens/Applications&WorkOrders/Hooks/useSavedPdfOrientation';
import { OperationType } from 'components/RecordsListView/constants';

export type OperationPdf = ReturnType<typeof useOperationPdf>;

interface UseQueuedPdfDownloadParams<TRecord extends { id?: string }> {
  record: TRecord | undefined;
  operationType: OperationType;
  userId: string;
  isOpen: boolean;
  /** The record failed to load, so a download asked for while loading is dropped. */
  hasFailed?: boolean;
  /** Not called once the download has been cancelled. */
  onError?: () => void;
  renderPdf: (pdf: OperationPdf, record: TRecord) => Promise<unknown>;
}

/**
 * Download-only PDF export for the Overview drawer — no orientation prompt, per
 * design; the user's saved orientation is applied instead. Leaving the Overview
 * cancels: rendering can't be aborted, so the finished file is discarded at the
 * delivery point instead.
 */
const useQueuedPdfDownload = <TRecord extends { id?: string }>({
  record,
  operationType,
  userId,
  isOpen,
  hasFailed = false,
  onError,
  renderPdf
}: UseQueuedPdfDownloadParams<TRecord>) => {
  const cancelledRef = useRef(false);
  const [isQueued, setIsQueued] = useState(false);
  const {
    orientation,
    exportWithSavedOrientation,
    cancelPendingExport
  } = useSavedPdfOrientation(userId);

  const pdf = useOperationPdf({
    orientation,
    operationType,
    shouldDeliver: () => !cancelledRef.current,
    onError: onError
      ? () => {
          if (cancelledRef.current) return;
          onError();
        }
      : undefined
  });
  const { downloadingPDF, cancelPdf } = pdf;

  // `shouldDeliver` isn't consulted until the render finishes, so on its own it
  // would leave the downloading state stuck on.
  useEffect(() => {
    if (!isOpen) {
      cancelledRef.current = true;
      cancelPdf();
    }
  }, [isOpen, cancelPdf]);

  // `isOpen` covers Close / X / Esc, but not the drawer being torn down while
  // still open by a route change.
  const cancelPdfRef = useRef(cancelPdf);
  cancelPdfRef.current = cancelPdf;

  useEffect(
    () => () => {
      cancelledRef.current = true;
      cancelPendingExport();
      cancelPdfRef.current();
    },
    [cancelPendingExport]
  );

  const runExportRef = useRef<() => Promise<void>>(async () => {});
  runExportRef.current = async () => {
    if (!record || cancelledRef.current) return;
    await renderPdf(pdf, record);
  };

  const startExport = useCallback(async () => {
    cancelledRef.current = false;
    await exportWithSavedOrientation(
      () => runExportRef.current(),
      () => cancelledRef.current
    );
  }, [exportWithSavedOrientation]);

  // The details query holds `{}` until its first response, so only an id
  // means the record has arrived.
  const hasRecord = Boolean(record?.id);

  // The button works while the record is still loading: the click is held
  // and the export starts once the record arrives.
  const downloadPdf = useCallback(async () => {
    if (hasRecord) {
      await startExport();
    } else if (!hasFailed) {
      setIsQueued(true);
    }
  }, [hasRecord, hasFailed, startExport]);

  useEffect(() => {
    if (!isQueued) return;
    if (!isOpen || hasFailed) {
      setIsQueued(false);
    } else if (hasRecord) {
      setIsQueued(false);
      startExport();
    }
  }, [isQueued, isOpen, hasFailed, hasRecord, startExport]);

  return {
    downloadPdf,
    isDownloading: isOpen && (downloadingPDF || isQueued)
  };
};

export default useQueuedPdfDownload;
